import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  appLicensesQuery,
  licenseAppQuery,
  licensedDevicesQuery,
  unlicensedDevicesQuery,
  type AppLicenseRow,
  type LicenseDevice,
} from '../api/inventoree'
import { ApiError } from '../api/client'
import { Spinner } from '../ui/Spinner'
import { dayMonthYear, escapeHtml, fromDuration, fromNow, stamp } from './format'

/**
 * Randul desfasurat al unei aplicatii din Software Licenses -
 * software_licenses/others/actionTabs.php si cele trei file: licenses.php,
 * licensedDevices.php, unlicensedDevices.php. Aceleasi gesturi din
 * ExpandRow (general.js): prima fila se deschide singura, clicul pe fila
 * deja deschisa o reincarca.
 */

export type LicenseTab = 'licenses' | 'licensed' | 'unlicensed'

/** device_icon() din devices_helper.php, cu clasa primita (rounded success / danger). */
export function deviceIcon(type: number | string | null, extra = ''): string {
  let icon = 'question-circle'
  let cls = extra
  switch (Number(type)) {
    case 1:
      icon = 'desktop'
      break
    case 2:
      icon = 'laptop'
      break
    case 3:
      icon = 'server'
      break
    case 4:
      icon = 'print'
      break
    case 6:
      icon = 'wifi'
      break
    case 11:
      icon = 'mobile'
      break
    case 12:
      icon = 'tablet'
      break
    case 55:
      icon = 'hdd-o'
      break
    case 71:
      icon = 'keyboard-o'
      break
    case 87:
      icon = 'tv'
      break
    default:
      icon = 'question-circle'
      cls = 'color-blue'
  }
  return `<i class="fas fa-${icon}${cls ? ` ${cls}` : ''}"></i>`
}

/** odPanelFailure(xhr) din general.js. */
export function failureText(error: unknown): string {
  if (error instanceof ApiError) return `HTTP ${error.status} - ${error.message}`
  return 'no answer from the server'
}

function Failure({ error }: { error: unknown }) {
  return (
    <div className="col-md-12">
      <div className="alert alert-danger">
        <strong>This did not load.</strong> <span style={{ fontSize: 12 }}>{failureText(error)}</span>
      </div>
    </div>
  )
}

/** ApplicationLicensesModel: License Type. */
export function licenseTypeHtml(row: Pick<AppLicenseRow, 'type_name'>): string {
  if (row.type_name) return `<span class="label label-primary">${escapeHtml(row.type_name)}</span>`
  return '<span class="label label-default">Not licensed</span>'
}

/** ApplicationLicensesModel: Start date - moment format "D MMM YYYY". */
export function licenseStartHtml(row: Pick<AppLicenseRow, 'start_date'>): string {
  const when = stamp(row.start_date)
  return when ? dayMonthYear(when) : ''
}

/** ApplicationLicensesModel: Status - expires / left / expired. */
export function licenseStatusHtml(row: Pick<AppLicenseRow, 'start_date' | 'end_date' | 'status'>, now: Date = new Date()): string {
  if (!row.end_date) return ''
  const end = stamp(row.end_date)
  if (!end) return ''
  if (row.status !== 'valid') {
    return '<span class="color-red" style="border: 1px solid #d83430 ;padding: 5px 10px;border-radius: 3px;">expired</span>'
  }
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const start = stamp(row.start_date)
  if (start && today.getTime() > start.getTime()) {
    return `<span class="color-quartz" style="border: 1px solid #089FA8;padding: 5px 10px;border-radius: 3px;">expires ${fromNow(end, today)}</span>`
  }
  const from = start ?? today
  return `<span class="color-quartz" style="border: 1px solid #089FA8;padding: 5px 10px;border-radius: 3px;">${fromDuration(end, from)} left</span>`
}

function LicensesTab({ id }: { id: number }) {
  const app = useQuery(licenseAppQuery(id))
  const table = useQuery(appLicensesQuery(id))
  if (app.isPending || table.isPending) return <Spinner />
  if (app.error) return <Failure error={app.error} />
  if (table.error) return <Failure error={table.error} />
  const name = app.data?.short_name ?? ''
  const rows = table.data?.data ?? []

  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>{name}</strong> licenses
        </h4>
      </header>
      <div className="panel-body">
        <div className="row">
          <div className="col-md-12">
            <table className="table table-striped table-hover dataTable licenses-table" width="100%" id="licensesTable">
              <thead>
                <tr>
                  <th className="td-small a-left" style={{ width: 150 }}>
                    License Type
                  </th>
                  <th className="td-small a-left" style={{ width: 150 }}>
                    Licenses
                  </th>
                  <th className="td-small a-left" style={{ width: 150 }}>
                    Active licenses
                  </th>
                  <th className="td-small a-left" style={{ width: 150 }}>
                    Start date
                  </th>
                  <th className="td-small a-left" style={{ width: 150 }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={String(row.DT_RowId ?? row.ID)} id={String(row.DT_RowId ?? `rowd_${row.ID}`)}>
                    <td className="td-small a-left" dangerouslySetInnerHTML={{ __html: licenseTypeHtml(row) }} />
                    <td className="td-small a-left">{row.licenses_number ? String(row.licenses_number) : ''}</td>
                    <td className="td-small a-left">{row.active_licenses ? String(row.active_licenses) : ''}</td>
                    <td className="td-small a-left">{licenseStartHtml(row)}</td>
                    <td className="td-small a-left" dangerouslySetInnerHTML={{ __html: licenseStatusHtml(row) }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}

function DeviceTile({ device, kind }: { device: LicenseDevice; kind: 'licensed' | 'unlicensed' }) {
  const icon = kind === 'licensed' ? deviceIcon(device.Tip, 'rounded success') : deviceIcon(device.Tip, 'rounded danger')
  const first = String(device.Prenume ?? '').toUpperCase()
  return (
    <div className="col-md-4">
      <div className="tiles-2 bg-normal">
        <div className="image-box">
          <div className="image" dangerouslySetInnerHTML={{ __html: icon }} />
        </div>
        <div className="content">
          <div className="title">
            <strong>{device.Denumire}</strong>
          </div>
          <div className="details">
            <span className="fas fa-user"></span> {first} {device.Nume ?? ''}
          </div>
          {kind === 'licensed' ? (
            <div className="details">
              <span className="fa fa-star color-warning"></span> {device.type_name ?? ''}
            </div>
          ) : (
            <div className="details color-red">
              <span className="far fa-star"></span> no license
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function EmptyDevices() {
  return (
    <div className="alert alert-danger">
      <span className="fas fa-info-circle"></span> No devices.
    </div>
  )
}

function LicensedTab({ id }: { id: number }) {
  const query = useQuery(licensedDevicesQuery(id))
  if (query.isPending) return <Spinner />
  if (query.error) return <Failure error={query.error} />
  const devices = query.data?.devices ?? []
  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Licensed</strong> devices
        </h4>
      </header>
      <div className="panel-body">
        <div className="row">
          {devices.length === 0 ? <EmptyDevices /> : devices.map((d, i) => <DeviceTile key={`${d.Denumire}-${i}`} device={d} kind="licensed" />)}
        </div>
      </div>
    </>
  )
}

function UnlicensedTab({ id }: { id: number }) {
  const query = useQuery(unlicensedDevicesQuery(id))
  if (query.isPending) return <Spinner />
  if (query.error) return <Failure error={query.error} />
  const devices = query.data?.devices ?? []
  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Unlicensed</strong> devices
        </h4>
      </header>
      <div className="panel-body">
        <div className="row">
          {devices.length === 0 ? <EmptyDevices /> : devices.map((d, i) => <DeviceTile key={`${d.Denumire}-${i}`} device={d} kind="unlicensed" />)}
        </div>
      </div>
    </>
  )
}

export function LicenseExpand({ applicationId }: { applicationId: number }) {
  const [tab, setTab] = useState<LicenseTab>('licenses')
  const [reload, setReload] = useState(0)
  const client = useQueryClient()

  const pick = (next: LicenseTab) => {
    if (next === tab) {
      client.removeQueries({ queryKey: ['inventoree', 'licenses'] })
      setReload((n) => n + 1)
    } else {
      setTab(next)
    }
  }

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          <li>
            <a
              href={`/inventoree/getApplicationLicenses/${applicationId}`}
              className={tab === 'licenses' ? 'action_button active' : 'action_button'}
              onClick={(e) => {
                e.preventDefault()
                pick('licenses')
              }}
            >
              Licenses
            </a>
          </li>
          <li>
            <a
              href={`/inventoree/getApplicationLicensedDevices/${applicationId}`}
              className={tab === 'licensed' ? 'action_button active' : 'action_button'}
              onClick={(e) => {
                e.preventDefault()
                pick('licensed')
              }}
            >
              Devices with license
            </a>
          </li>
          <li>
            <a
              href={`/inventoree/getApplicationUnlicensedDevices/${applicationId}`}
              className={tab === 'unlicensed' ? 'action_button color-red active' : 'action_button color-red'}
              onClick={(e) => {
                e.preventDefault()
                pick('unlicensed')
              }}
            >
              Devices without license
            </a>
          </li>
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === 'licenses' && <LicensesTab key={`l${reload}`} id={applicationId} />}
          {tab === 'licensed' && <LicensedTab key={`d${reload}`} id={applicationId} />}
          {tab === 'unlicensed' && <UnlicensedTab key={`u${reload}`} id={applicationId} />}
        </div>
      </div>
    </div>
  )
}
